import React, { Fragment, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { InternalApiAlertType, InternalApiManualAlert } from '../../api-internal';
import Table from '../../table/Table';
import { Button, Dropdown } from 'react-bootstrap';
import { NotesModal, NotesModalFormValues } from '../NotesModal';
import { getPersonDisplayName, isNavigableCellClicked } from '../../common/helper';
import FormattedDateTime from '../../common/datetime/FormattedDateTime';
import Has from '../../auth/Has';
import {
  CAN_ADD_NOTE,
  CAN_MODIFY_ALERT,
  CAN_MODIFY_ALERT_THROUGH_FILTER,
  CAN_READ_ALERT,
} from '../../auth/permissions';
import { DropdownWrapper } from '../../common/DropdownWrapper';
import { useLocation, useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import { noteInternalApi } from '../../apis';
import { showFeedback } from '../../feedback/feedbackReducer';
import AlertAssignment from '../../common/status/AlertAssignment';
import AlertRowStatus from '../../alerts/AlertRowStatus';
import { getManualAlertPath } from '../../navigation/helper';
import { useAlertStatuses } from '../../screening/alert-status/alertStatusesHelper';
import CreateManualAlertButton from '../../alerts/manual/CreateManualAlertButton';
import { getRationaleLabel } from '../../alerts/manual/rationaleHelper';
import { useQueryAttributeMappings } from '../../schema/mappings/mappingsActions';
import { useMe } from '../../organisation/useMe';
import { formatAmount } from '../../schema/helper';
import DataLoadingErrorFallback from '../../common/errors/DataLoadingErrorFallback';
import { SalvProgressCircle } from '@salvapp/ui-components';

interface TableAttribute {
  key: string;
  sortable: boolean;
}

interface AlertProps {
  page: number;
  size: number;
  total: number;
  loading: boolean;
  alerts: Array<InternalApiManualAlert>;
  attributes: TableAttribute[];
  onUpdate: () => void;
  onUpdatePage?: (page: number) => void;
  title?: string;
  expandable?: boolean;
  expandedByDefault?: boolean;
  showActions?: boolean;
  showInteractiveStatus?: boolean;
  createAlertProps?: {
    personId: string;
    transactionId?: string;
    personName?: string;
    transactionAmount?: string;
  };
}

export const ManualAlerts = ({
  alerts,
  page,
  size,
  total,
  loading,
  onUpdate,
  onUpdatePage,
  attributes,
  title = 'Manual alerts',
  expandable = false,
  expandedByDefault = true,
  showActions = true,
  showInteractiveStatus = true,
  createAlertProps,
}: AlertProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [activeStatuses] = useAlertStatuses();
  const { currentOrganisation } = useMe();
  const {
    data: attributeMappings,
    isPending: isAttributeMappingsPending,
    isError: isAttributeMappingsError,
  } = useQueryAttributeMappings();

  const tableTitle = useMemo(
    () => (
      <div className='d-flex align-items-center justify-content-between w-100'>
        <span>
          {title} {!loading && <span className='sanction-amount-info'>({total})</span>}
        </span>
        {createAlertProps && (
          <Has anyPermissions={[CAN_READ_ALERT, CAN_MODIFY_ALERT_THROUGH_FILTER]}>
            <CreateManualAlertButton
              personId={createAlertProps.personId}
              transactionId={createAlertProps.transactionId}
              personName={createAlertProps.personName}
              transactionAmount={createAlertProps.transactionAmount}
              onAlertCreated={onUpdate}
              variant='secondary'
            />
          </Has>
        )}
      </div>
    ),
    [total, loading, title, createAlertProps, onUpdate]
  );

  if (isAttributeMappingsError) {
    return <DataLoadingErrorFallback message={'Could not load Attribute Mappings'} />;
  } else if (isAttributeMappingsPending) {
    return <SalvProgressCircle ariaLabel={'Loading Attribute Mappings'} />;
  }

  const updatePage = (page: number) => {
    onUpdatePage && onUpdatePage(page);
  };

  const onSubmitNote = async (alertId: number, values: NotesModalFormValues) => {
    if (alertId) {
      const { id } = await noteInternalApi.createNote({
        internalApiCreateNote: {
          alertId,
          content: values.content,
          alertType: InternalApiAlertType.MANUAL,
        },
      });
      dispatch(showFeedback({ message: `Note #${id} added!`, success: true }));
      onUpdate();
    }
  };

  const navigateTo = (alert: InternalApiManualAlert) => {
    if (location.pathname !== getManualAlertPath(alert.id)) {
      return navigate(getManualAlertPath(alert.id));
    }
  };

  const renderObjectDetails = (alert: InternalApiManualAlert) => {
    if (alert.transaction) {
      const amount = formatAmount(
        parseFloat(alert.transaction.attributes?.amount || '0'),
        currentOrganisation?.currency
      );
      return (
        <>
          Transaction: {alert.transaction.id} • {amount}
        </>
      );
    }
    const personName = alert.person ? getPersonDisplayName(alert.person, attributeMappings.map) : undefined;
    return <>Person: {personName ?? '-'}</>;
  };

  const tableAttributes = showActions ? attributes : attributes.filter((attr) => attr.key !== 'Actions');
  return (
    <Table
      title={tableTitle}
      responsive={false}
      total={total}
      page={page}
      size={size}
      items={alerts}
      fetchPage={updatePage}
      attributes={tableAttributes}
      fetching={loading}
      expandable={expandable}
      expandedByDefault={expandedByDefault}
      emptyMessage='No manual alerts'
      body={(alerts) =>
        alerts.map((alert: InternalApiManualAlert, index) => (
          <Fragment key={alert.id}>
            <tr
              className={`custom-table-row-${index % 2} clickable-row`}
              onClick={(event: React.MouseEvent) => {
                isNavigableCellClicked(event) && navigateTo(alert);
              }}
            >
              <td>
                <Link to={getManualAlertPath(alert.id)}>{alert.id}</Link>
              </td>
              <td>{renderObjectDetails(alert)}</td>
              <td>{getRationaleLabel(alert.rationale)}</td>
              <td>
                <FormattedDateTime date={alert.createdTime} showTime />
              </td>
              <td>{alert.assignee?.name || '-'}</td>
              <td>
                {showInteractiveStatus ? (
                  <AlertRowStatus alert={alert} onChange={() => onUpdate()} alertType={InternalApiAlertType.MANUAL} />
                ) : (
                  activeStatuses.find((it) => it.id === alert.statusId)?.textName || alert.status
                )}
              </td>
              {showActions && (
                <td>
                  <DropdownWrapper align='end'>
                    <Dropdown.Toggle as={Button} size='sm' className='btn-white' id='actions-dropdown'>
                      •••
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Has allPermissions={CAN_ADD_NOTE}>
                        <NotesModal onSubmit={(values) => onSubmitNote(alert.id, values)} />
                      </Has>
                      <Has allPermissions={CAN_MODIFY_ALERT}>
                        <AlertAssignment
                          alert={alert}
                          alertType={InternalApiAlertType.MANUAL}
                          afterChange={() => Promise.resolve()}
                          permissions={[CAN_MODIFY_ALERT]}
                        />
                      </Has>
                    </Dropdown.Menu>
                  </DropdownWrapper>
                </td>
              )}
            </tr>
          </Fragment>
        ))
      }
    />
  );
};

export default ManualAlerts;
